import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import * as agentApi from '@/lib/agents/api';
import type { AgentType, LLMProvider, CreateAgentInput } from '@/lib/agents/types';
import { AGENT_TYPE_INFO, LLM_PROVIDER_INFO } from '@/lib/agents/types';
import {
  MessageSquare,
  Search,
  ListTodo,
  BarChart,
  Code,
  Headphones,
  Globe,
  Settings,
  Users,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const AGENT_TYPE_ICONS: Record<AgentType, React.ElementType> = {
  chat: MessageSquare,
  research: Search,
  task: ListTodo,
  'data-analysis': BarChart,
  'code-assistant': Code,
  support: Headphones,
  'web-browsing': Globe,
  'multi-agent': Users,
  custom: Settings,
};

export function FormBuilder() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [agentType, setAgentType] = useState<AgentType>('chat');
  const [llmProvider, setLlmProvider] = useState<LLMProvider>('anthropic');
  const [model, setModel] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [maxIterations, setMaxIterations] = useState(10);
  const [isPublic, setIsPublic] = useState(false);

  // Persona
  const [personaName, setPersonaName] = useState('');
  const [personaDescription, setPersonaDescription] = useState('');
  const [personaTone, setPersonaTone] = useState('professional');

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('Agent name is required');
      return;
    }

    setLoading(true);
    try {
      const input: CreateAgentInput = {
        name: name.trim(),
        description: description.trim() || undefined,
        agentType,
        config: {
          llmProvider,
          model: model || undefined,
          systemPrompt: systemPrompt.trim() || undefined,
          temperature,
          maxIterations,
          persona: personaName
            ? {
                name: personaName,
                description: personaDescription || undefined,
                tone: personaTone,
              }
            : undefined,
        },
        isPublic,
      };

      const response = await agentApi.createAgent(input);
      toast.success('Agent created successfully');
      navigate({ to: `/agents/${response.data.id}` });
    } catch (error) {
      toast.error((error as Error).message || 'Failed to create agent');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Progress indicator */}
      <div className="flex items-center justify-center gap-2 mb-8">
        {[1, 2, 3, 4].map((s) => (
          <div
            key={s}
            className={`h-2 w-16 rounded-full ${
              s <= step ? 'bg-primary' : 'bg-muted'
            }`}
          />
        ))}
      </div>

      {/* Step 1: Basic Info */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>Name and describe your agent</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Agent Name *</Label>
              <Input
                id="name"
                placeholder="My Assistant"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Describe what this agent does..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Agent Type</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {Object.entries(AGENT_TYPE_INFO).map(([type, info]) => {
                  const Icon = AGENT_TYPE_ICONS[type as AgentType];
                  const isSelected = agentType === type;
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setAgentType(type as AgentType)}
                      className={`p-3 rounded-lg border text-left transition-colors ${
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <Icon className="h-5 w-5 mb-2" />
                      <div className="font-medium text-sm">{info.label}</div>
                      <div className="text-xs text-muted-foreground line-clamp-2">
                        {info.description}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={() => setStep(2)} disabled={!name.trim()}>
                Next
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: LLM Configuration */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>LLM Configuration</CardTitle>
            <CardDescription>Choose the language model for your agent</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>LLM Provider</Label>
              <Select value={llmProvider} onValueChange={(v) => setLlmProvider(v as LLMProvider)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(LLM_PROVIDER_INFO).map(([provider, info]) => (
                    <SelectItem key={provider} value={provider}>
                      {info.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Model</Label>
              <Select value={model || 'default'} onValueChange={(v) => setModel(v === 'default' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a model (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default</SelectItem>
                  {LLM_PROVIDER_INFO[llmProvider].models.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Temperature: {temperature}</Label>
              <Slider
                value={[temperature]}
                onValueChange={([v]) => setTemperature(v)}
                min={0}
                max={2}
                step={0.1}
              />
              <p className="text-xs text-muted-foreground">
                Lower = more focused, Higher = more creative
              </p>
            </div>

            <div className="space-y-2">
              <Label>Max Tool Iterations: {maxIterations}</Label>
              <Slider
                value={[maxIterations]}
                onValueChange={([v]) => setMaxIterations(v)}
                min={1}
                max={20}
                step={1}
              />
              <p className="text-xs text-muted-foreground">
                Maximum number of tool call loops before stopping
              </p>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button onClick={() => setStep(3)}>Next</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: System Prompt & Persona */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle>System Prompt & Persona</CardTitle>
            <CardDescription>Define how your agent behaves</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="systemPrompt">System Prompt</Label>
              <Textarea
                id="systemPrompt"
                placeholder="You are a helpful assistant that..."
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                rows={5}
              />
              <p className="text-xs text-muted-foreground">
                Instructions that define the agent&apos;s behavior and capabilities
              </p>
            </div>

            <div className="border-t pt-4 space-y-4">
              <h4 className="font-medium">Persona (Optional)</h4>

              <div className="space-y-2">
                <Label htmlFor="personaName">Persona Name</Label>
                <Input
                  id="personaName"
                  placeholder="Alex"
                  value={personaName}
                  onChange={(e) => setPersonaName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="personaDescription">Persona Description</Label>
                <Textarea
                  id="personaDescription"
                  placeholder="A friendly expert in..."
                  value={personaDescription}
                  onChange={(e) => setPersonaDescription(e.target.value)}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Communication Tone</Label>
                <Select value={personaTone} onValueChange={setPersonaTone}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="friendly">Friendly</SelectItem>
                    <SelectItem value="casual">Casual</SelectItem>
                    <SelectItem value="formal">Formal</SelectItem>
                    <SelectItem value="technical">Technical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)}>
                Back
              </Button>
              <Button onClick={() => setStep(4)}>Next</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Review & Create */}
      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle>Review & Create</CardTitle>
            <CardDescription>Confirm your agent configuration</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground">Name</Label>
                <p className="font-medium">{name}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Type</Label>
                <p className="font-medium">{AGENT_TYPE_INFO[agentType].label}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Provider</Label>
                <p className="font-medium">{LLM_PROVIDER_INFO[llmProvider].label}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Model</Label>
                <p className="font-medium">{model || 'Default'}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Temperature</Label>
                <p className="font-medium">{temperature}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Max Iterations</Label>
                <p className="font-medium">{maxIterations}</p>
              </div>
            </div>

            {description && (
              <div>
                <Label className="text-muted-foreground">Description</Label>
                <p>{description}</p>
              </div>
            )}

            {systemPrompt && (
              <div>
                <Label className="text-muted-foreground">System Prompt</Label>
                <p className="text-sm bg-muted p-2 rounded">{systemPrompt}</p>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Switch checked={isPublic} onCheckedChange={setIsPublic} />
              <Label>Make this agent public (visible to organization)</Label>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(3)}>
                Back
              </Button>
              <Button onClick={handleSubmit} disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Agent
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
